// assets/workshop-calendar.js
class WorkshopCalendar {
  constructor(container) {
    this.container = container;
    this.workshopData = JSON.parse(document.getElementById('workshop-data').textContent);
    
    // Debug: Log all workshop data first
    console.log('Workshop data:', this.workshopData);
    this.debugWorkshopDates();
    
    // Find all months that have workshops
    this.availableMonths = this.findAllMonthsWithWorkshops();
    console.log('Available months with workshops:', this.availableMonths);
    
    // Set current month to first available month
    this.currentMonthIndex = 0;
    this.currentDate = this.availableMonths.length > 0 ? this.availableMonths[0] : new Date();
    
    this.init();
  }

  debugWorkshopDates() {
    console.log('=== DEBUGGING WORKSHOP DATES ===');
    this.workshopData.workshops.forEach((workshop, index) => {
      console.log(`Workshop ${index + 1}:`, {
        name: workshop.name,
        short_name: workshop.short_name,
        day_time: workshop.day_time,
        variant_position: workshop.variant_position,
        parsed_month: this.extractMonthFromWorkshop(workshop),
        parsed_year: this.extractYearFromWorkshop(workshop)
      });
    });
  }

  extractMonthFromWorkshop(workshop) {
    // Extract month number from workshop day_time
    if (workshop.day_time.includes('/')) {
      const monthMatch = workshop.day_time.match(/\/(\d+)/);
      return monthMatch ? parseInt(monthMatch[1]) : null;
    }
    return null;
  }

  extractYearFromWorkshop(workshop) {
    // For now, assume current year unless we can parse it differently
    // The format "6-27/10" doesn't include year, so we need to make assumptions
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1-based month
    const workshopMonth = this.extractMonthFromWorkshop(workshop);
    
    if (workshopMonth) {
      // If workshop month is in the past compared to current month, assume next year
      if (workshopMonth < currentMonth) {
        return currentYear + 1;
      }
      return currentYear;
    }
    
    return currentYear;
  }

  init() {
    this.renderCalendar();
    this.bindEvents();
    this.updateNavigationButtons();
  }

  findAllMonthsWithWorkshops() {
    const monthsWithWorkshops = [];
    const uniqueMonths = new Set(); // Prevent duplicates
    
    // Process each workshop to extract its specific month/year
    this.workshopData.workshops.forEach(workshop => {
      const workshopMonth = this.extractMonthFromWorkshop(workshop);
      const workshopYear = this.extractYearFromWorkshop(workshop);
      
      if (workshopMonth && workshopYear) {
        const monthKey = `${workshopYear}-${workshopMonth}`;
        
        if (!uniqueMonths.has(monthKey)) {
          uniqueMonths.add(monthKey);
          // Create date object for the first day of the workshop month
          const workshopDate = new Date(workshopYear, workshopMonth - 1, 1);
          monthsWithWorkshops.push(workshopDate);
          
          console.log(`Added workshop month: ${workshopDate.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}`);
        }
      }
    });
    
    // Sort months chronologically
    monthsWithWorkshops.sort((a, b) => a.getTime() - b.getTime());
    
    return monthsWithWorkshops;
  }

  monthHasWorkshops(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Check each day of the month to see if it has workshops
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const testDate = new Date(year, month, day);
      const dateString = this.formatDateString(testDate);
      
      const workshopsOnDate = this.getWorkshopsForDate(dateString);
      if (workshopsOnDate.length > 0) {
        return true;
      }
    }
    
    return false;
  }

  formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  navigateToPreviousMonth() {
    if (this.currentMonthIndex > 0) {
      this.currentMonthIndex--;
      this.currentDate = this.availableMonths[this.currentMonthIndex];
      this.renderCalendar();
      this.updateNavigationButtons();
    }
  }

  navigateToNextMonth() {
    if (this.currentMonthIndex < this.availableMonths.length - 1) {
      this.currentMonthIndex++;
      this.currentDate = this.availableMonths[this.currentMonthIndex];
      this.renderCalendar();
      this.updateNavigationButtons();
    }
  }

  updateNavigationButtons() {
    const prevButton = this.container.querySelector('.prev-month');
    const nextButton = this.container.querySelector('.next-month');
    
    // Disable/enable navigation buttons based on available months
    if (this.currentMonthIndex <= 0) {
      prevButton.disabled = true;
      prevButton.style.opacity = '0.3';
      prevButton.style.cursor = 'not-allowed';
    } else {
      prevButton.disabled = false;
      prevButton.style.opacity = '1';
      prevButton.style.cursor = 'pointer';
    }
    
    if (this.currentMonthIndex >= this.availableMonths.length - 1) {
      nextButton.disabled = true;
      nextButton.style.opacity = '0.3';
      nextButton.style.cursor = 'not-allowed';
    } else {
      nextButton.disabled = false;
      nextButton.style.opacity = '1';
      nextButton.style.cursor = 'pointer';
    }

    // Update month display - clean month name only
    const monthDisplay = this.container.querySelector('.current-month');
    const currentMonthText = new Intl.DateTimeFormat('pl-PL', { 
      month: 'long', 
      year: 'numeric' 
    }).format(this.currentDate);
    
    monthDisplay.textContent = currentMonthText;
  }

  renderCalendar() {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    
    console.log(`Rendering calendar for: ${year}-${month + 1}`);
    
    // Generate calendar days
    const daysContainer = this.container.querySelector('#calendar-days');
    daysContainer.innerHTML = '';

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    // Fix Monday start for Polish calendar
    const mondayStart = (firstDay.getDay() + 6) % 7;
    startDate.setDate(startDate.getDate() - mondayStart);

    // Generate full weeks (6 weeks = 42 days)
    for (let i = 0; i < 42; i++) {
      const currentDay = new Date(startDate);
      currentDay.setDate(startDate.getDate() + i);
      
      const dayElement = this.createDayElement(currentDay);
      daysContainer.appendChild(dayElement);
      
      // Stop if we've covered the month and completed the week
      if (currentDay.getMonth() !== month && currentDay.getDay() === 0) {
        break;
      }
    }
  }

  createDayElement(date) {
    const dayDiv = document.createElement('div');
    dayDiv.className = 'calendar-day';
    
    const isCurrentMonth = date.getMonth() === this.currentDate.getMonth();
    
    // Fix date string calculation to avoid timezone issues
    const dateString = this.formatDateString(date);
    
    if (!isCurrentMonth) {
      dayDiv.classList.add('other-month');
    }

    // Get workshops for this date (max 3)
    const workshopsToday = this.getWorkshopsForDate(dateString);
    
    if (workshopsToday.length > 0) {
      console.log(`Found ${workshopsToday.length} workshops on ${dateString}:`, workshopsToday);
    }
    
    dayDiv.innerHTML = `
      <div class="day-header">
        <span class="day-number">${date.getDate()}</span>
      </div>
      <div class="day-events">
        ${workshopsToday.slice(0, 3).map(workshop => this.renderWorkshopEvent(workshop)).join('')}
        ${workshopsToday.length > 3 ? '<div class="more-events">...</div>' : ''}
      </div>
    `;

    return dayDiv;
  }

  renderWorkshopEvent(workshop) {
    const workshopImage = workshop.image && workshop.image !== '' ? workshop.image : null;
    const timeDuration = this.formatTimeDuration(workshop.time_start, workshop.time_end);
    
    // Get day of week from workshop.day_time
    const dayOfWeek = this.extractDayOfWeek(workshop.day_time);
    
    // Get short name from metafield
    const shortName = workshop.short_name || '';
    
    console.log('Rendering workshop event:', {
      name: workshop.name,
      short_name: workshop.short_name,
      shortName: shortName,
      dayOfWeek: dayOfWeek,
      timeDuration: timeDuration
    });
    
    // Get variant color based on position
    const variantClass = this.getVariantColorClass(workshop.variant_position);
    
    return `
      <div class="workshop-event ${variantClass}" 
           data-url="${workshop.url || '#'}" 
           data-variant-id="${workshop.id}"
           data-type="${workshop.type}" 
           data-variant-position="${workshop.variant_position}">
        <div class="workshop-content">
          ${workshopImage ? `<img src="${workshopImage}" alt="${workshop.name}" class="workshop-image">` : ''}
          <div class="workshop-details">
            ${shortName ? `<div class="workshop-short-name">${shortName}</div>` : ''}
            <div class="workshop-day">${dayOfWeek}</div>
            <div class="workshop-time">${timeDuration}</div>
          </div>
        </div>
      </div>
    `;
  }

  extractDayOfWeek(dayTime) {
    // Extract day of week from formats like "Poniedziałek 6-27/10" or "Sobota 11/10"
    const dayTimeParts = dayTime.split(' ');
    const dayOfWeek = dayTimeParts[0];
    
    // Return capitalized day name
    return dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1).toLowerCase();
  }

  getVariantColorClass(variantPosition) {
    switch(variantPosition) {
      case 1: return 'variant-1'; // Blue #1961AC
      case 2: return 'variant-2'; // Pink #F9C6C0
      case 3: return 'variant-3'; // Green #7F8A82
      case 4: return 'variant-4'; // Black #121212
      case 5: return 'variant-5'; // White #FFFFFF with dark text
      case 6: return 'variant-6'; // Light Gray #D0D0D0 with dark text
      default: return 'variant-1'; // Default to blue
    }
  }

  getWorkshopsForDate(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);
    const targetDate = new Date(year, month - 1, day);
    
    const workshops = this.workshopData.workshops.filter(workshop => {
      const matchesDate = this.isWorkshopOnDate(workshop, targetDate);
      const hasSpots = workshop.spots_available > 0;
      
      if (matchesDate) {
        console.log(`Workshop "${workshop.name}" matches date ${dateString}:`, {
          day_time: workshop.day_time,
          short_name: workshop.short_name,
          variant_position: workshop.variant_position,
          spots_available: workshop.spots_available
        });
      }
      
      return matchesDate && hasSpots;
    });

    // Parse times and sort
    return workshops.map(workshop => {
      const times = this.parseWorkshopTimes(workshop.day_time);
      return {
        ...workshop,
        time_start: times.start,
        time_end: times.end,
        date: dateString
      };
    }).sort((a, b) => {
      const timeA = this.parseTime(a.time_start);
      const timeB = this.parseTime(b.time_start);
      return timeA - timeB;
    });
  }

  formatTimeDuration(timeStart, timeEnd) {
    // Format time as duration (14:00-16:00)
    if (!timeStart) return '';
    
    const formattedStart = this.formatTime24h(timeStart);
    const formattedEnd = this.formatTime24h(timeEnd);
    
    if (formattedEnd && formattedEnd !== '') {
      return `${formattedStart}-${formattedEnd}`;
    }
    
    return formattedStart;
  }

  formatTime24h(timeString) {
    // Handle various time formats and ensure 24h display
    if (!timeString) return '';
    
    // If already in 24h format (HH:MM), return as is
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      return timeString;
    }
    
    // Handle other formats and convert to 24h
    try {
      const time = new Date(`2000-01-01 ${timeString}`);
      return time.toLocaleTimeString('pl-PL', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } catch (e) {
      return timeString; // Return original if parsing fails
    }
  }

  parseTime(timeString) {
    // Convert time to minutes for sorting
    const time24h = this.formatTime24h(timeString);
    const [hours, minutes] = time24h.split(':').map(Number);
    return hours * 60 + minutes;
  }

  bindEvents() {
    // Month navigation - use new filtered navigation methods
    this.container.querySelector('.prev-month').addEventListener('click', () => {
      this.navigateToPreviousMonth();
    });

    this.container.querySelector('.next-month').addEventListener('click', () => {
      this.navigateToNextMonth();
    });

    // Workshop event clicks - use event delegation
    this.container.addEventListener('click', (e) => {
      const workshopEvent = e.target.closest('.workshop-event');
      if (workshopEvent) {
        const url = workshopEvent.getAttribute('data-url');
        if (url && url !== '#') {
          window.location.href = url;
        }
      }
    });
  }

  // Helper method to check if workshop occurs on given date
  isWorkshopOnDate(workshop, targetDate) {
    console.log('Checking workshop:', workshop.day_time, 'against date:', targetDate.toDateString());
    
    const dayNames = {
      'poniedziałek': 1, 'wtorek': 2, 'środa': 3, 'czwartek': 4,
      'piątek': 5, 'sobota': 6, 'niedziela': 0
    };
    
    // Check if day_time contains month number (new format)
    if (workshop.day_time.includes('/') || workshop.day_time.includes('.')) {
      // Extract month from patterns like "/10" or ".10"
      let workshopMonth = null;
      
      // Try "/10" pattern first
      const slashMonthMatch = workshop.day_time.match(/\/(\d+)/);
      if (slashMonthMatch) {
        workshopMonth = parseInt(slashMonthMatch[1]) - 1;
      }
      
      // Try ".10" pattern if no slash pattern found
      if (!workshopMonth && workshopMonth !== 0) {
        const dotMonthMatch = workshop.day_time.match(/\.(\d+)/);
        if (dotMonthMatch) {
          workshopMonth = parseInt(dotMonthMatch[1]) - 1;
        }
      }
      
      console.log('Workshop month:', workshopMonth, 'Target month:', targetDate.getMonth());
      
      // Check month match
      if (workshopMonth !== targetDate.getMonth()) {
        return false;
      }
      
      // Simple approach: extract all numbers that could be days
      const targetDay = targetDate.getDate();
      console.log('Target day:', targetDay);
      
      // For "10 i 24.10" - look for numbers before "i" and between "i" and "."
      if (workshop.day_time.includes(' i ')) {
        const iPattern = workshop.day_time.match(/(\d+)\s+i\s+(\d+)/);
        if (iPattern) {
          const firstDay = parseInt(iPattern[1]);
          const secondDay = parseInt(iPattern[2]);
          console.log('Found "i" pattern - days:', firstDay, secondDay);
          return targetDay === firstDay || targetDay === secondDay;
        }
      }
      
      // For "11.10" - single day
      console.log('Checking single day pattern for:', workshop.day_time);
      console.log('Contains "i"?', workshop.day_time.includes(' i '));
      
      const singleDayPattern = workshop.day_time.match(/(\d+)\.(\d+)/);
      console.log('Single day regex match:', singleDayPattern);
      
      if (singleDayPattern && !workshop.day_time.includes(' i ')) {
        const day = parseInt(singleDayPattern[1]);
        const month = parseInt(singleDayPattern[2]);
        console.log('Found single day pattern - day:', day, 'month:', month);
        console.log('Target day:', targetDay, 'matches?', targetDay === day);
        return targetDay === day;
      }
      
      // For "6-27/10" - range with day of week check OR single day like "11/10"
      const rangePattern = workshop.day_time.match(/(\d+)(?:-(\d+))?\/\d+/);
      if (rangePattern) {
        const startDay = parseInt(rangePattern[1]);
        const endDay = rangePattern[2] ? parseInt(rangePattern[2]) : startDay; // If no end day, use start day
        console.log('Found range/single pattern:', startDay, endDay === startDay ? '(single day)' : '-' + endDay);
        
        // Check if day is in range
        if (targetDay >= startDay && targetDay <= endDay) {
          // Also check day of week for these patterns
          const dayTimeParts = workshop.day_time.split(' ');
          const workshopDayOfWeek = dayNames[dayTimeParts[0].toLowerCase()];
          console.log('Day of week check - workshop:', workshopDayOfWeek, 'target:', targetDate.getDay());
          return targetDate.getDay() === workshopDayOfWeek;
        }
      }
      
      return false;
    } else {
      // Old format: fallback to month_name (weekly recurring)
      const monthNames = {
        'styczeń': 0, 'luty': 1, 'marzec': 2, 'kwiecień': 3,
        'maj': 4, 'czerwiec': 5, 'lipiec': 6, 'sierpień': 7,
        'wrzesień': 8, 'październik': 9, 'listopad': 10, 'grudzień': 11
      };
      
      const workshopMonth = monthNames[workshop.month_name.toLowerCase()];
      const dayTimeParts = workshop.day_time.split(' ');
      const workshopDay = dayNames[dayTimeParts[0].toLowerCase()];
      
      return targetDate.getMonth() === workshopMonth && 
             targetDate.getDay() === workshopDay;
    }
  }

  // Helper method to parse workshop times
  parseWorkshopTimes(dayTime) {
    // Handle new format "Poniedziałek 6-27/10 | 18:30-20:30"
    if (dayTime.includes('|')) {
      const [schedulePart, timePart] = dayTime.split(' | ');
      const [start, end] = timePart.split('-');
      return {
        start: start,
        end: end
      };
    }
    
    // Fallback to old formats for backwards compatibility
    const parts = dayTime.split(' ');
    
    if (parts.length >= 3) {
      // Old format: "Poniedziałek 6-27 18:30-20:30"
      const timePart = parts[2];
      const [start, end] = timePart.split('-');
      return {
        start: start,
        end: end
      };
    } else if (parts.length >= 2) {
      // Oldest format: "Poniedziałek 18-20" 
      const timePart = parts[1];
      const [start, end] = timePart.split('-');
      return {
        start: start + ':00',
        end: end + ':00'
      };
    }
    
    return { start: '', end: '' };
  }
}

// Initialize calendar when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const calendarElement = document.querySelector('[data-calendar-wrapper]');
  if (calendarElement) {
    new WorkshopCalendar(calendarElement);
  }
});